import { Row, Select, FormInstance } from "antd";
import { CloseOutlined } from "@ant-design/icons";
import { CriteriaOption } from "../../../../types/bulkEmails/CriteriaOption";
import { CriteriaRowModel } from "../../../../types/bulkEmails/CriteriaRowModel";
import { memo, useCallback, useMemo } from "react";

export const CriteriaRow = memo(({
  form,
  row,
  options,
  selectedOption,
  selectedKeys,
  availableKeys,
  onChange,
  onRemove
}: {
  form: FormInstance;
  row: CriteriaRowModel;
  options: CriteriaOption[];
  selectedOption: CriteriaOption;
  selectedKeys: string;
  availableKeys: string;
  onChange: (row: CriteriaRowModel) => void;
  onRemove: (rowId: string) => void;
}) => {
  const handleOnRemove = useCallback(() => {
    onRemove(row.id);
  }, [row, onRemove]);

  const handleValueChange = useCallback(
    (value: unknown) => {
      onChange({ ...row, value });
    },
    [onChange, row]
  );

  const handleOptionChange = useCallback(
    (optionKey: string) => {
      onChange({ ...row, optionKey, value: undefined });
    },
    [onChange, row]
  );

  const selectOptions = useMemo(
    () => {
      const selected = new Set(selectedKeys.split("|"));
      const available = new Set(availableKeys.split("|"));

      return options.map((option) => ({
        label: option.title,
        value: option.key,
        disabled: option.key !== row.optionKey && (selected.has(option.key) || !available.has(option.key))
      }));
    },
    [options, row.optionKey, selectedKeys, availableKeys]
  );

  return (
    <Row className="mx-2 mb-2 align-start">
      <Row className="criteria-option">
        <Select
          disabled={!row.removable}
          value={row.optionKey}
          className="w-100"
          options={selectOptions}
          onChange={handleOptionChange}
        />
      </Row>
      <Row className="justify-center equality-sign">
        <span className="text">&nbsp;=&nbsp;</span>
      </Row>
      <Row className="criteria-value">
        {selectedOption?.render?.({
          value: row.value,
          form: form,
          formKey: selectedOption.formKeys[0],
          formKeys: selectedOption.formKeys,
          onChange: handleValueChange
        })}
      </Row>
      {row.removable && (
        <Row className="justify-center remove-criteria">
          <CloseOutlined className="ml-2 text-md title logo-cursor" onClick={handleOnRemove} />
        </Row>
      )}
    </Row>
  );
});
